import React, { useState, useEffect, useRef, useMemo } from 'react';
import httpHelper from 'utils/http-helper';
import URL_CONSTANTS from 'constants/url-constants';
import { Method_TYPE } from 'utils/http-helper';
import DataTable from 'react-data-table-component';
import ReportHeader from './ReportHeader';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import {
  Printer,
  FileText,
  FileSpreadsheet,
  Search,
  X,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
} from 'lucide-react';
import { convertToIST } from 'utils/formtaDate';

const SORT_OPTIONS = [
  { value: 'name', label: 'Name' },
  { value: 'age', label: 'Age' },
  { value: 'district', label: 'District' },
  { value: 'samithi', label: 'Samithi' },
  { value: 'sector', label: 'Sector' },
];

const SORTABLE_COLUMNS = SORT_OPTIONS.map((option) => option.value);

const SEARCHABLE_FIELDS = [
  'name',
  'age',
  'bloodgroup',
  'district',
  'samithi',
  'mobile',
  'saiconnectedid',
  'aadhar',
  'certificateno',
  'sector',
  'deploymentpoint',
  'reason',
  'removedOn',
  'address',
];

const SortHeader = ({ label, field, sortBy, sortDirection, onSort }) => {
  const isActive = sortBy === field;

  return (
    <button
      type="button"
      onClick={() => onSort(field)}
      className={`inline-flex items-center gap-1.5 font-semibold transition-colors ${
        isActive ? 'text-primary' : 'text-gray-700 hover:text-primary'
      }`}
    >
      <span>{label}</span>
      {isActive ? (
        sortDirection === 'asc' ? (
          <ArrowUp size={14} className="shrink-0 text-primary" />
        ) : (
          <ArrowDown size={14} className="shrink-0 text-primary" />
        )
      ) : (
        <ArrowUpDown size={14} className="shrink-0 text-gray-400" />
      )}
    </button>
  );
};

export default function RegisteredMemberDetails() {
  const [registeredMemberDetails, setRegisteredMemberDetails] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('sector');
  const [sortDirection, setSortDirection] = useState('asc');
  const printRef = useRef();

  useEffect(() => {
    getRegisteredMemberDetails();
  }, []);

  const getRegisteredMemberDetails = async () => {
    try {
      const data = await httpHelper(URL_CONSTANTS.GET_REGISTATION_MEMBER_DETAILS, Method_TYPE.GET);
      const resultSet = data?.reports?.sevaDalList?.map((tableData, index) => ({
        slno: index + 1,
        name: tableData?.Name,
        age: tableData?.age,
        bloodgroup: tableData?.bloodGroup,
        district: tableData?.district,
        samithi: tableData?.samithi,
        mobile: tableData?.mobNo,
        saiconnectedid: tableData?.saiOneId,
        aadhar: tableData?.aadharNo,
        certificateno: tableData?.scNo,
        sector: tableData?.Sector,
        deploymentpoint: tableData?.DutyPoint,
        reason: tableData?.ReasonForRemove,
        removedOn: convertToIST(tableData?.RemovedOn) || '----',
        address: tableData?.Address,
        is_blocked: Boolean(tableData?.is_blocked ?? tableData?.isBlocked),
      })) || [];
      setRegisteredMemberDetails(resultSet);
    } catch (err) {
      console.error('Error fetching member details:', err);
      setRegisteredMemberDetails([]);
    }
  };

  const handleColumnSort = (field) => {
    if (!SORTABLE_COLUMNS.includes(field)) return;
    if (sortBy === field) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(field);
      setSortDirection('asc');
    }
  };

  const filteredAndSortedData = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();

    let filtered = registeredMemberDetails;
    if (query) {
      filtered = registeredMemberDetails.filter((row) =>
        SEARCHABLE_FIELDS.some((field) => {
          const value = row[field];
          return value != null && String(value).toLowerCase().includes(query);
        })
      );
    }

    const sorted = [...filtered].sort((a, b) => {
      const aVal = a[sortBy];
      const bVal = b[sortBy];

      if (sortBy === 'age') {
        const aNum = Number(aVal) || 0;
        const bNum = Number(bVal) || 0;
        return sortDirection === 'asc' ? aNum - bNum : bNum - aNum;
      }

      const aStr = (aVal ?? '').toString().toLowerCase();
      const bStr = (bVal ?? '').toString().toLowerCase();
      if (aStr < bStr) return sortDirection === 'asc' ? -1 : 1;
      if (aStr > bStr) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [registeredMemberDetails, searchTerm, sortBy, sortDirection]);

  const getBatchInfo = () => {
    const batchData = JSON.parse(localStorage.getItem('currentBatch'));
    if (batchData) {
      return `${batchData.name}:${convertToIST(batchData.startDate)} - ${convertToIST(batchData.endDate)}`;
    }
    return '';
  };

  const makeSortableColumn = (label, field) => ({
    name: (
      <SortHeader
        label={label}
        field={field}
        sortBy={sortBy}
        sortDirection={sortDirection}
        onSort={handleColumnSort}
      />
    ),
    selector: (row) => row[field],
    sortable: false,
    cell: (row) => <div data-title={label}>{row[field]}</div>,
  });

  const columns = [
    {
      name: 'Sl.No',
      selector: (row, index) => index + 1,
      sortable: false,
      width: '80px',
    },
    {
      name: (
        <SortHeader
          label="Name"
          field="name"
          sortBy={sortBy}
          sortDirection={sortDirection}
          onSort={handleColumnSort}
        />
      ),
      selector: (row) => row.name,
      sortable: false,
      cell: (row) => (
        <div data-title="Name" className="flex items-center space-x-2">
          <span className={row.is_blocked ? "font-semibold text-red-700" : ""}>{row.name}</span>
          {row.is_blocked && (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-700 border border-red-300">
              Blocked
            </span>
          )}
        </div>
      ),
    },
    makeSortableColumn('Age', 'age'),
    {
      name: 'Blood Group',
      selector: (row) => row.bloodgroup,
      sortable: false,
      cell: (row) => <div data-title="Blood Group">{row.bloodgroup}</div>,
    },
    makeSortableColumn('District', 'district'),
    makeSortableColumn('Samithi', 'samithi'),
    {
      name: 'Mobile',
      selector: (row) => row.mobile,
      sortable: false,
      cell: (row) => <div data-title="Mobile">{row.mobile}</div>,
    },
    {
      name: 'Sai Connect',
      selector: (row) => row.saiconnectedid,
      sortable: false,
      cell: (row) => <div data-title="Sai Connect">{row.saiconnectedid}</div>,
    },
    {
      name: 'Aadhar',
      selector: (row) => row.aadhar,
      sortable: false,
      cell: (row) => <div data-title="Aadhar">{row.aadhar}</div>,
    },
    {
      name: 'Certificate No',
      selector: (row) => row.certificateno,
      sortable: false,
      cell: (row) => <div data-title="Certificate No">{row.certificateno}</div>,
    },
    makeSortableColumn('Sector', 'sector'),
    {
      name: 'Deployment Point',
      selector: (row) => row.deploymentpoint,
      sortable: false,
      cell: (row) => <div data-title="Deployment Point">{row.deploymentpoint}</div>,
    },
    {
      name: 'Reason',
      selector: (row) => row.reason,
      sortable: false,
      cell: (row) => <div data-title="Reason">{row.reason}</div>,
    },
    {
      name: 'Removed On',
      selector: (row) => row.removedOn,
      sortable: false,
      cell: (row) => <div data-title="Removed On">{row.removedOn}</div>,
    },
    {
      name: 'Address',
      selector: (row) => row.address,
      sortable: false,
      cell: (row) => <div data-title="Address">{row.address}</div>,
    },
  ];

  const printColumns = [
    { name: 'Sl.No', selector: (row, index) => index + 1 },
    { name: 'Name', selector: (row) => row.name },
    { name: 'Age', selector: (row) => row.age },
    { name: 'Blood Group', selector: (row) => row.bloodgroup },
    { name: 'District', selector: (row) => row.district },
    { name: 'Samithi', selector: (row) => row.samithi },
    { name: 'Mobile', selector: (row) => row.mobile },
    { name: 'Sai Connect', selector: (row) => row.saiconnectedid },
    { name: 'Aadhar', selector: (row) => row.aadhar },
    { name: 'Certificate No', selector: (row) => row.certificateno },
    { name: 'Sector', selector: (row) => row.sector },
    { name: 'Deployment Point', selector: (row) => row.deploymentpoint },
    { name: 'Reason', selector: (row) => row.reason },
    { name: 'Removed On', selector: (row) => row.removedOn },
    { name: 'Address', selector: (row) => row.address },
  ];

  const conditionalRowStyles = [
    {
      when: (row) => row.is_blocked,
      style: {
        backgroundColor: 'rgba(254, 226, 226, 0.45)',
        color: '#991b1b',
      },
    },
  ];

  const customStyles = {
    headRow: {
      style: {
        backgroundColor: '#f8fafc',
        borderBottomWidth: '1px',
        borderBottomColor: '#e2e8f0',
        minHeight: '56px',
      },
    },
    headCells: {
      style: {
        whiteSpace: 'normal',
        wordBreak: 'break-word',
        overflowWrap: 'break-word',
        lineHeight: '1.4',
        minHeight: '56px',
        paddingTop: '10px',
        paddingBottom: '10px',
        fontSize: '13px',
      },
    },
    cells: {
      style: {
        whiteSpace: 'normal',
        wordBreak: 'break-word',
        overflowWrap: 'break-word',
        lineHeight: '1.4',
        minHeight: '64px',
        paddingTop: '8px',
        paddingBottom: '8px',
      },
    },
    rows: {
      style: {
        borderBottomColor: '#f1f5f9',
        '&:hover': {
          backgroundColor: '#f8fafc',
        },
      },
    },
  };

  const handlePrint = () => {
    const printContent = printRef.current.innerHTML;
    const printWindow = window.open('', '', 'width=1000,height=700');
    printWindow.document.write('<html><head><title>Registered Sevadal Details</title>');
    printWindow.document.write('<style>table { width: 100%; border-collapse: collapse; font-size: 12px; } th, td { border: 1px solid black; padding: 4px; text-align: left; }</style>');
    printWindow.document.write('</head><body>');
    printWindow.document.write(`<h2>TamilNadu Prasanthi Seva - Registered Sevadal Details</h2><p><strong>${getBatchInfo()}</strong></p>`);
    printWindow.document.write(printContent);
    printWindow.document.write('</body></html>');
    printWindow.document.close();
    printWindow.print();
  };

  const handleDownloadPDF = () => {
    const doc = new jsPDF('l');
    doc.text('Registered Sevadal Details', 14, 10);
    doc.text(`${getBatchInfo()}`, 14, 18);
    autoTable(doc, {
      startY: 20,
      head: [['Sl.No', 'Name', 'Age', 'Blood Group', 'District', 'Samithi', 'Mobile', 'Sai Connect', 'Aadhar', 'Certificate No', 'Sector', 'Deployment Point', 'Reason', 'Removed On', 'Address']],
      body: filteredAndSortedData.map((row, index) => [
        index + 1,
        row.name,
        row.age,
        row.bloodgroup,
        row.district,
        row.samithi,
        row.mobile,
        row.saiconnectedid,
        row.aadhar,
        row.certificateno,
        row.sector,
        row.deploymentpoint,
        row.reason,
        row.removedOn,
        row.address,
      ]),
    });
    doc.save('registered-sevadal-details.pdf');
  };

  const handleDownloadXLS = () => {
    const exportData = filteredAndSortedData.map((item, index) => ({
      'Sl.No': index + 1,
      Name: item.name,
      Age: item.age,
      'Blood Group': item.bloodgroup,
      District: item.district,
      Samithi: item.samithi,
      Mobile: item.mobile,
      'Sai Connect': item.saiconnectedid,
      Aadhar: item.aadhar,
      'Certificate No': item.certificateno,
      Sector: item.sector,
      'Deployment Point': item.deploymentpoint,
      Reason: item.reason,
      'Removed On': item.removedOn,
      Address: item.address,
    }));
    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Sevadal Details');
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const file = new Blob([excelBuffer], { type: 'application/octet-stream' });
    saveAs(file, 'registered-sevadal-details.xlsx');
  };

  return (
    <>
      <div className="rounded-xl border border-slate-200 bg-white shadow-sm my-4 overflow-hidden">
        <div className="flex flex-col lg:flex-row lg:items-end gap-3 p-4 border-b border-slate-100 bg-slate-50/80">
          <div className="relative flex-1 max-w-md w-full">
            <label htmlFor="member-search" className="block text-xs font-medium text-slate-600 mb-1.5">
              Search
            </label>
            <div className="relative">
              <Search
                size={16}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
              />
              <input
                id="member-search"
                type="search"
                placeholder="Search name, district, samithi, sector..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full rounded-lg border border-slate-200 bg-white pl-9 pr-9 py-2.5 text-sm text-slate-800 placeholder:text-slate-400 shadow-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
              />
              {searchTerm && (
                <button
                  type="button"
                  onClick={() => setSearchTerm('')}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1 rounded-md text-slate-400 hover:text-slate-600 hover:bg-slate-100"
                  aria-label="Clear search"
                >
                  <X size={14} />
                </button>
              )}
            </div>
          </div>

          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label htmlFor="member-sort-by" className="block text-xs font-medium text-slate-600 mb-1.5">
                Sort by
              </label>
              <select
                id="member-sort-by"
                value={sortBy}
                onChange={(e) => {
                  setSortBy(e.target.value);
                  setSortDirection('asc');
                }}
                className="rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-800 shadow-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary min-w-[140px]"
              >
                {SORT_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="member-sort-dir" className="block text-xs font-medium text-slate-600 mb-1.5">
                Order
              </label>
              <select
                id="member-sort-dir"
                value={sortDirection}
                onChange={(e) => setSortDirection(e.target.value)}
                className="rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-800 shadow-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary min-w-[140px]"
              >
                <option value="asc">Ascending</option>
                <option value="desc">Descending</option>
              </select>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2 lg:ml-auto lg:pb-0.5">
            <span className="inline-flex items-center rounded-full bg-primary/10 text-primary px-3 py-1.5 text-xs font-semibold">
              {filteredAndSortedData.length}
              {searchTerm.trim() && registeredMemberDetails.length !== filteredAndSortedData.length
                ? ` of ${registeredMemberDetails.length}`
                : ''}{' '}
              members
            </span>

            {registeredMemberDetails.length > 0 && (
              <div className="flex items-center gap-2">
                <button
                  onClick={handlePrint}
                  className="bg-primary text-white px-3 py-2 rounded-lg inline-flex items-center gap-1.5 text-xs font-medium hover:opacity-90 transition-opacity"
                >
                  <Printer size={15} className="min-w-4" />
                  <span className="hidden sm:inline">Print</span>
                </button>
                <button
                  onClick={handleDownloadPDF}
                  className="bg-blue-600 text-white px-3 py-2 rounded-lg inline-flex items-center gap-1.5 text-xs font-medium hover:bg-blue-700 transition-colors"
                >
                  <FileText size={15} className="min-w-4" />
                  <span className="hidden sm:inline">PDF</span>
                </button>
                <button
                  onClick={handleDownloadXLS}
                  className="bg-emerald-600 text-white px-3 py-2 rounded-lg inline-flex items-center gap-1.5 text-xs font-medium hover:bg-emerald-700 transition-colors"
                >
                  <FileSpreadsheet size={15} className="min-w-4" />
                  <span className="hidden sm:inline">XLS</span>
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="px-4 py-2 text-xs text-slate-500 border-b border-slate-100">
          Click column headers with <ArrowUpDown size={12} className="inline mx-0.5 text-slate-400" /> to sort by Name, Age, District, Samithi, or Sector
        </div>

        <div className="px-4 pt-3">
          <ReportHeader header="Registration Member Details" />
        </div>

        <div className="px-2 pb-2">
          <DataTable
            columns={columns}
            data={filteredAndSortedData}
            fixedHeader
            fixedHeaderScrollHeight="500px"
            customStyles={customStyles}
            conditionalRowStyles={conditionalRowStyles}
            className="reports-table"
            noDataComponent={
              <div className="py-10 text-sm text-slate-500">
                {searchTerm.trim() ? 'No members match your search' : 'No member details available'}
              </div>
            }
          />
        </div>
      </div>

      <div ref={printRef} className="hidden">
        <table>
          <thead>
            <tr>
              {printColumns.map((col, idx) => (
                <th key={idx}>{col.name}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredAndSortedData.map((item, idx) => (
              <tr key={idx}>
                {printColumns.map((col, colIdx) => (
                  <td key={colIdx}>{col.selector(item, idx)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}
